import React from 'react';
import { Award, Users, TrendingUp, Shield, Zap, Globe } from 'lucide-react';
import './About.scss';

const About: React.FC = () => {
  const stats = [
    {
      number: '2025',
      label: 'Founded',
      description: 'Started with a vision to democratize trading'
    },
    {
      number: '$2.4B+',
      label: 'Trading Volume',
      description: 'Processed across all platforms'
    },
    {
      number: '10,000+',
      label: 'Active Users',
      description: 'Trust our platform daily'
    },
    {
      number: '50+',
      label: 'Exchanges',
      description: 'Integrated globally'
    }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'We prioritize the security of your assets above all else, implementing industry-leading security measures.'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'Constantly pushing the boundaries of trading technology with cutting-edge AI and machine learning.'
    },
    {
      icon: Users,
      title: 'Customer Success',
      description: 'Your success is our success. We provide dedicated support and resources to help you achieve your goals.'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Connecting traders worldwide with access to global markets and opportunities.'
    }
  ];

  const team = [
    {
      name: 'Sarah Chen',
      role: 'CEO & Co-Founder',
      background: 'Former large-scale cryptocurrency trader, ex-DHS analyst',
      image: '/team/unknown.jpg'
    },
    {
      name: 'Connor Windish',
      role: 'CTO & Co-Founder',
      background: 'Former HalogenChain Lead Developer, Blockchain & AI Architect',
      image: '/team/connor-windish.jpg'
    },
    {
      name: 'David Kim',
      role: 'Head of Product',
      background: 'Former Coinbase Product Lead, Expert in crypto markets',
      image: '/team/david-kim.jpg'
    },
    {
      name: 'Emily Johnson',
      role: 'Head of Security',
      background: 'Ex-NSA Cybersecurity Specialist, 20+ years in financial security',
      image: '/team/emily-johnson.jpg'
    }
  ];

  return (
    <section className="about section">
      <div className="container">
        {/* Main About Content */}
        <div className="about__content">
          <div className="about__text">
            <h2 className="about__title">
              About <span className="gradient-text">CoinVAT LLC</span>
            </h2>
            <div className="about__subtitle">
              <strong>AI Trading Bot + AI-Assisted Trading Platform</strong>
            </div>
            <p className="about__description">
              CoinVAT LLC was founded with a simple yet ambitious mission: to revolutionize 
              cryptocurrency trading through both intelligent automation and AI-powered assistance. 
              Our platform serves two distinct trading approaches: fully automated AI trading bots 
              for hands-off trading, and intelligent AI assistants for enhanced manual trading.
            </p>
            <p className="about__description">
              Since our inception in 2025, we've processed over $2.4 billion in trading volume 
              and helped thousands of traders achieve consistent profitability. Whether you prefer 
              set-and-forget automation or AI-enhanced manual control, our platform provides the 
              tools and technology needed to succeed in today's dynamic crypto markets.
            </p>
            
            <div className="about__highlights">
              <div className="about__highlight">
                <Zap className="about__highlight-icon" />
                <span>Automated bot trading with 24/7 execution</span>
              </div>
              <div className="about__highlight">
                <Shield className="about__highlight-icon" />
                <span>AI-powered trading assistance for manual traders</span>
              </div>
              <div className="about__highlight">
                <Award className="about__highlight-icon" />
                <span>Award-winning dual-mode trading technology</span>
              </div>
            </div>
          </div>

          <div className="about__visual">
            <div className="about__stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="about__stat-card">
                  <div className="about__stat-number">{stat.number}</div>
                  <div className="about__stat-label">{stat.label}</div>
                  <div className="about__stat-description">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="about__values">
          <h3 className="about__values-title">Our Core Values</h3>
          <div className="about__values-grid">
            {values.map((value, index) => (
              <div key={index} className="about__value-card">
                <div className="about__value-icon">
                  <value.icon size={24} />
                </div>
                <h4 className="about__value-title">{value.title}</h4>
                <p className="about__value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team Section */}
        {/*<div className="about__team">
          <div className="about__team-header">
            <h3 className="about__team-title">Meet Our Leadership Team</h3>
            <p className="about__team-subtitle">
              Industry veterans with decades of combined experience in finance, technology, and cryptocurrency.
            </p>
          </div>
          
          <div className="about__team-grid">
            {team.map((member, index) => (
              <div key={index} className="about__team-card">
                <div className="about__team-avatar">
                  <div className="about__team-avatar-placeholder">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <div className="about__team-info">
                  <h4 className="about__team-name">{member.name}</h4>
                  <div className="about__team-role">{member.role}</div>
                  <p className="about__team-background">{member.background}</p>
                </div>
              </div>
            ))}
          </div>
        </div>*/}

        {/* Mission Statement */}
        {/*<div className="about__mission">
          <div className="about__mission-content">
            <h3 className="about__mission-title">Our Mission</h3>
            <blockquote className="about__mission-quote">
              "To democratize sophisticated trading technology through decentralized governance, 
              offering both fully automated AI trading bots and intelligent trading assistance, 
              while ensuring our community maintains collective control over the platform's evolution 
              and maintains the highest standards of security, reliability, and performance."
            </blockquote>
            <div className="about__mission-author">
              <strong>Connor Windish</strong>, Co-Founder
            </div>
          </div>
        </div>*/}

        {/* Technology Section */}
        <div className="about__technology">
          <div className="about__tech-content">
            <h3 className="about__tech-title">Cutting-Edge Dual-Mode Technology</h3>
            <p className="about__tech-description">
              Our platform leverages the latest advances in artificial intelligence, cloud computing, 
              and financial technology to deliver both fully automated trading bots and intelligent 
              trading assistance, providing unparalleled flexibility and performance.
            </p>
            
            <div className="about__tech-features">
              <div className="about__tech-feature">
                <div className="about__tech-feature-title">Automated Trading Bots</div>
                <div className="about__tech-feature-description">
                  Fully autonomous AI bots that execute trades 24/7 using advanced machine learning algorithms
                </div>
              </div>
              <div className="about__tech-feature">
                <div className="about__tech-feature-title">AI Trading Assistant</div>
                <div className="about__tech-feature-description">
                  Intelligent co-pilot providing real-time insights, trade suggestions, and risk analysis
                </div>
              </div>
              <div className="about__tech-feature">
                <div className="about__tech-feature-title">Unified Infrastructure</div>
                <div className="about__tech-feature-description">
                  Scalable, redundant systems supporting both trading modes with 99.9% uptime guarantee
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
